import React from "react";

export default function CheckoutOrderSummary({
  checkoutType,
  cartItems,
  getItemTotals,
  addServiceCharge,
  serviceChargeAmount,
  total,
  isLoading = false,
  placingOrder = false,
  checkingStock = false,
  onPlaceOrder,
  buttonText = "Send Order To Kitchen",
  disablePay = false,
  deliveryFee = 0,
  secondaryButtonText = "",
  onSecondaryAction = null,
  disableSecondaryAction = false,
}) {
  const subtotal = cartItems
    .reduce((acc, cartItem) => {
      const itemTotals = getItemTotals(cartItem) || {};
      const itemTotal = Number(itemTotals.total || 0);
      const lineTotal = itemTotal * (cartItem.quantity || 1);
      return acc + lineTotal;
    }, 0)
    .toFixed(2);

  return (
    <div className="overflow-hidden rounded-[28px] border border-black/10 bg-white shadow-[0_18px_45px_rgba(0,0,0,0.22),0_4px_10px_rgba(0,0,0,0.08)]">
      <div className="border-b border-black/10 p-6">
        <h3 className="text-[18px] font-bold text-black">Order Summary</h3>
      </div>

      <div className="max-h-[320px] overflow-y-auto p-6">
        {cartItems.length === 0 ? (
          <p className="text-sm text-black/50">Your cart is empty.</p>
        ) : (
          <div>
            {cartItems.map((cartItem, index) => {
              const item = cartItem.item || cartItem;
              const quantity = cartItem.quantity || 1;

              const itemTotals = getItemTotals(cartItem) || {};
              const itemTotal = Number(itemTotals.total || 0);
              const options = Array.isArray(itemTotals.options)
                ? itemTotals.options
                : [];

              const finalTotal = (itemTotal * quantity).toFixed(2);
              const basePrice = Number(item?.price || 0).toFixed(2);

              return (
                <div key={`${item?.id || "item"}-${index}`}>
                  <div className="py-4 first:pt-0 last:pb-0">
                    <div className="flex gap-4">
                      <div className="min-w-0 flex-grow">
                        <div className="flex justify-between gap-3">
                          <h4 className="font-semibold text-black">
                            {item?.item_name}
                          </h4>

                          <span className="whitespace-nowrap font-medium text-black">
                            £{basePrice}
                          </span>
                        </div>

                        {options.length > 0 && (
                          <div className="mt-1 space-y-[2px] text-xs text-black/50">
                            {options.map((opt, i) => (
                              <div
                                key={i}
                                className="flex items-start justify-between gap-3"
                              >
                                <span className="min-w-0 flex-1">
                                  {opt?.name || ""}
                                </span>
                                <span className="whitespace-nowrap">
                                  £{Number(opt?.price || 0).toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="mt-3 flex items-center justify-between gap-3">
                          <span className="rounded-full bg-black/5 px-3 py-1 text-xs font-medium text-black/70">
                            Qty: {quantity}
                          </span>

                          <span className="font-semibold text-black">
                            £{finalTotal}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {index !== cartItems.length - 1 && (
                    <div className="mt-4 w-full border-t border-[#e5e5e5]" />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-3 bg-black/[0.035] p-6">
        <div className="flex justify-between text-black/60">
          <span>Subtotal</span>
          <span>£{subtotal}</span>
        </div>

        {checkoutType === "delivery" && deliveryFee !== null && (
          <div className="flex justify-between text-black/60">
            <span>Delivery Fee</span>
            <span>£{Number(deliveryFee).toFixed(2)}</span>
          </div>
        )}

        {addServiceCharge && Number(serviceChargeAmount) > 0 && (
          <div className="flex justify-between text-black/60">
            <span>Service Charge</span>
            <span>£{Number(serviceChargeAmount).toFixed(2)}</span>
          </div>
        )}

        <div className="mt-3 flex items-center justify-between border-t border-black/10 pt-3">
          <span className="text-lg font-bold text-black">Total</span>
          <span className="text-2xl font-bold text-[#fa9324]">
            £{Number(total).toFixed(2)}
          </span>
        </div>

        <div className="mt-4 space-y-6">
          <button
            type="button"
            onClick={onPlaceOrder}
            disabled={isLoading || disablePay || cartItems.length === 0}
            className={`flex w-full items-center justify-center gap-2 rounded-full py-4 font-bold text-md transition-all duration-200 ${
              isLoading || disablePay || cartItems.length === 0
                ? "cursor-not-allowed bg-[#fa9324]/50 text-black shadow-none"
                : "bg-[#fa9324] text-black shadow-[0_12px_30px_rgba(0,0,0,0.40)] hover:-translate-y-[2px] hover:shadow-[0_20px_45px_rgba(0,0,0,0.50)] hover:opacity-90 active:translate-y-[1px] active:shadow-[0_8px_18px_rgba(0,0,0,0.40)]"
            }`}
          >
            {placingOrder
              ? "Processing..."
              : checkingStock
                ? "Checking..."
                : buttonText}
            <span className="material-icons">arrow_forward</span>
          </button>

          {checkoutType === "pickup" &&
            secondaryButtonText &&
            onSecondaryAction && (
              <button
                type="button"
                onClick={onSecondaryAction}
                disabled={
                  isLoading || disableSecondaryAction || cartItems.length === 0
                }
                className={`flex w-full items-center justify-center gap-2 rounded-full border py-4 font-bold text-md transition-all duration-200 ${
                  isLoading || disableSecondaryAction || cartItems.length === 0
                    ? "cursor-not-allowed border-black/10 bg-black/5 text-black/40 shadow-none"
                    : "border-black/20 bg-[#A8A8A9] text-black shadow-[0_10px_24px_rgba(0,0,0,0.12)] hover:bg-[#d4d4d4] hover:-translate-y-[2px] hover:shadow-[0_16px_30px_rgba(0,0,0,0.18)] active:translate-y-[1px]"
                }`}
              >
                {placingOrder ? "Processing..." : secondaryButtonText}
                <span className="material-icons">payments</span>
              </button>
            )}
        </div>

        {checkoutType === "delivery" && (
          <p className="mt-2 text-center text-xs text-black/50">
            Your order will be confirmed after payment is completed.
          </p>
        )}

        <div className="mt-6 flex flex-col items-center gap-2">
          <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-black/35">
            <span className="material-icons text-xs">lock</span>
            {checkoutType === "delivery" || checkoutType === "pickup"
              ? "Secure Payment"
              : "Secure Checkout"}
          </div>
        </div>
      </div>
    </div>
  );
}
